"use client";

import React, { Suspense, useEffect, useState, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  IconButton,
  Badge,
  Divider,
  Tooltip
} from "@mui/material";
import {
  Business as BusinessIcon,
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Menu as MenuIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { logEventos } from "../../../services/services";
import { EquipmentTable } from "./equipos/v2/equipment-table";
import { CorrectiveMaintenanceList } from "./correctivos/v2/corrective-maintenance-list";
import { PlantSectionsList } from "./plant-sections-list";
import { NotificarEncargadoButton } from "./NotificarEncargadoButton";
import { UrlApi } from "../../../services/apirest";
import CountUpEffect from "../utils/count-effect";
import Navbar from "../../Navbar";
import { useReload } from "../context/ReloadContext";
import { usePlanta } from "../context/PlantaContext";
import { useVencimientoAlertas } from "../hooks/useVencimientoAlertas";

const WorkOrdersList = React.lazy(() => import("./ots/v2/work-orders-list"));
const ExpirationsList = React.lazy(() => import("./vencimientos/v2/expiration-list"));
const KpisDashboard = React.lazy(() => import("./kpis-dashboard"));

// ─── Constants ────────────────────────────────────────────────────────────────

const SIDEBAR_WIDTH = 232;

const NAV_ITEMS = [
  { id: 0, label: "Resumen", Icon: BusinessIcon },
  { id: 1, label: "Equipos", Icon: BuildIcon },
  { id: 2, label: "Órdenes de Trabajo", Icon: AssignmentIcon },
  { id: 3, label: "Vencimientos", Icon: AccessTimeIcon },
  { id: 4, label: "Correctivos", Icon: WarningIcon },
  { id: 5, label: "KPIs", Icon: TrendingUpIcon },
];

const STATS = [
  { key: "total_equipos", label: "Equipos", Icon: BuildIcon, gradient: ["#1d4ed8", "#3b82f6"] },
  { key: "total_ordenes", label: "Órdenes activas", Icon: AssignmentIcon, gradient: ["#0369a1", "#0ea5e9"] },
  { key: "total_vencimientos", label: "Vencimientos", Icon: AccessTimeIcon, gradient: ["#92400e", "#f59e0b"] },
  { key: "total_mantenimientos", label: "Correctivos", Icon: WarningIcon, gradient: ["#991b1b", "#ef4444"] },
];

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ label, Icon, value, gradient, delay }) {
  return (
    <Box
      sx={{
        flex: "1 1 150px",
        minWidth: 120,
        maxWidth: 200,
        background: `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 100%)`,
        borderRadius: 2,
        p: 2,
        color: "#fff",
        position: "relative",
        overflow: "hidden",
        "@keyframes statIn": {
          "0%": { opacity: 0, transform: "translateY(8px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
        animation: "statIn 0.4s ease both",
        animationDelay: `${delay}ms`,
        "&::before": {
          content: '""',
          position: "absolute",
          right: -10,
          bottom: -10,
          width: 60,
          height: 60,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.15)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.875, opacity: 0.85 }}>
        <Icon sx={{ fontSize: 13 }} />
        <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.02em" }}>
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: { xs: "1.5rem", sm: "1.875rem" },
          fontWeight: 800,
          lineHeight: 1,
        }}
      >
        <CountUpEffect value={value} />
      </Typography>
    </Box>
  );
}

// ─── SidebarContent ────────────────────────────────────────────────────────────

function SidebarContent({ activeSection, onSelectSection, counters, collapsed, onToggle }) {
  const { plantas, plantaSeleccionada, setPlantaSeleccionada } = usePlanta();
  const [anchorEl, setAnchorEl] = useState(null);

  const currentPlanta = useMemo(
    () => plantas?.find((p) => p.id_planta === plantaSeleccionada),
    [plantas, plantaSeleccionada]
  );

  const hasUrgency = (counters?.correctivos_vencen_7_dias ?? 0) > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#0f172a",
        color: "#f8fafc",
        userSelect: "none",
        overflow: "hidden",
      }}
    >
      {/* Brand + toggle */}
      <Box sx={{ px: collapsed ? 1 : 2, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between" }}>
          {/* Icon always visible */}
          <Box sx={{ display: "flex", alignItems: "center", gap: collapsed ? 0 : 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 32, height: 32, borderRadius: 1.5, bgcolor: "#3b82f6",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}
            >
              <BuildIcon sx={{ fontSize: 17, color: "#fff" }} />
            </Box>
            {!collapsed && (
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 9, lineHeight: 1, opacity: 0.4, textTransform: "uppercase", letterSpacing: "0.1em", mb: 0.25 }}>
                  Módulo
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                  Mantenimiento
                </Typography>
              </Box>
            )}
          </Box>

          {/* Toggle button (desktop only via prop) */}
          {onToggle && (
            <Tooltip title={collapsed ? "Expandir menú" : "Contraer menú"} placement="right">
              <IconButton
                size="small"
                onClick={onToggle}
                sx={{
                  ml: collapsed ? 0 : 0.5,
                  color: "#475569",
                  "&:hover": { color: "#f8fafc", bgcolor: "rgba(255,255,255,0.06)" },
                }}
              >
                {collapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.07)" }} />

      {/* Plant selector — hidden when collapsed */}
      {!collapsed && (
        <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
          <Typography sx={{ fontSize: 10, opacity: 0.4, textTransform: "uppercase", letterSpacing: "0.09em", mb: 1, display: "block" }}>
            Planta activa
          </Typography>
          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              px: 1.5, py: 0.875, borderRadius: 1.5,
              border: "1px solid rgba(255,255,255,0.1)", cursor: "pointer",
              transition: "all 0.15s ease",
              "&:hover": { bgcolor: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.2)" },
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: plantaSeleccionada ? "#22c55e" : "#475569", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, flex: 1, opacity: plantaSeleccionada ? 1 : 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {currentPlanta?.sucursal ?? "Sin seleccionar"}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 16, opacity: 0.5, flexShrink: 0 }} />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: { bgcolor: "#1e293b", color: "#f8fafc", border: "1px solid rgba(255,255,255,0.08)", minWidth: SIDEBAR_WIDTH - 32, mt: 0.5 },
            }}
          >
            <MenuItem
              onClick={() => { setPlantaSeleccionada(null); setAnchorEl(null); }}
              sx={{ fontSize: 13, color: "#94a3b8", "&:hover": { bgcolor: "rgba(255,255,255,0.05)" } }}
            >
              Sin filtro de planta
            </MenuItem>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.07)" }} />
            {(plantas ?? []).map((p) => (
              <MenuItem
                key={p.id_planta}
                onClick={() => { setPlantaSeleccionada(p.id_planta); setAnchorEl(null); }}
                selected={p.id_planta === plantaSeleccionada}
                sx={{
                  fontSize: 13,
                  "&.Mui-selected": { bgcolor: "rgba(59,130,246,0.2)", color: "#93c5fd" },
                  "&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
                  "&.Mui-selected:hover": { bgcolor: "rgba(59,130,246,0.25)" },
                }}
              >
                {p.sucursal}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}

      {/* Collapsed: plant dot indicator */}
      {collapsed && plantaSeleccionada && (
        <Tooltip title={currentPlanta?.sucursal ?? "Planta seleccionada"} placement="right">
          <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#22c55e" }} />
          </Box>
        </Tooltip>
      )}

      <Divider sx={{ borderColor: "rgba(255,255,255,0.07)" }} />

      {/* Navigation */}
      <Box sx={{ flex: 1, py: 1.5, px: collapsed ? 0.5 : 1, overflowY: "auto" }}>
        {!collapsed && (
          <Typography sx={{ fontSize: 10, opacity: 0.4, textTransform: "uppercase", letterSpacing: "0.09em", px: 1.5, mb: 0.75, display: "block" }}>
            Secciones
          </Typography>
        )}
        <List dense disablePadding>
          {NAV_ITEMS.map(({ id, label, Icon: NavIcon }) => {
            const isActive = activeSection === id;
            const showBadge = id === 4 && hasUrgency;
            const iconEl = showBadge ? (
              <Badge color="error" variant="dot"><NavIcon sx={{ fontSize: 18 }} /></Badge>
            ) : (
              <NavIcon sx={{ fontSize: 18 }} />
            );

            if (collapsed) {
              return (
                <Tooltip key={id} title={label} placement="right">
                  <ListItemButton
                    onClick={() => onSelectSection(id)}
                    sx={{
                      justifyContent: "center",
                      borderRadius: 1.5,
                      mb: 0.25,
                      py: 1,
                      mx: 0.5,
                      borderLeft: isActive ? "2px solid #3b82f6" : "2px solid transparent",
                      bgcolor: isActive ? "rgba(59,130,246,0.12)" : "transparent",
                      color: isActive ? "#93c5fd" : "rgba(248,250,252,0.55)",
                      "&:hover": { bgcolor: isActive ? "rgba(59,130,246,0.18)" : "rgba(255,255,255,0.05)", color: isActive ? "#93c5fd" : "#f8fafc" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, color: "inherit" }}>{iconEl}</ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              );
            }

            return (
              <ListItemButton
                key={id}
                onClick={() => onSelectSection(id)}
                sx={{
                  borderRadius: 1.5, mb: 0.25, pl: 1.5, py: 0.875,
                  borderLeft: isActive ? "2px solid #3b82f6" : "2px solid transparent",
                  bgcolor: isActive ? "rgba(59,130,246,0.12)" : "transparent",
                  color: isActive ? "#93c5fd" : "rgba(248,250,252,0.55)",
                  transition: "all 0.15s ease",
                  "&:hover": { bgcolor: isActive ? "rgba(59,130,246,0.18)" : "rgba(255,255,255,0.05)", color: isActive ? "#93c5fd" : "#f8fafc" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>{iconEl}</ListItemIcon>
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: 13, fontWeight: isActive ? 600 : 400 }} />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* Urgency alert */}
      {hasUrgency && (
        <Box sx={{ px: collapsed ? 0.5 : 2, pb: 2 }}>
          {collapsed ? (
            <Tooltip title={`${counters.correctivos_vencen_7_dias} correctivos vencen en 7 días`} placement="right">
              <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
                <WarningIcon sx={{ fontSize: 18, color: "#fca5a5" }} />
              </Box>
            </Tooltip>
          ) : (
            <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", display: "flex", alignItems: "center", gap: 0.75 }}>
              <WarningIcon sx={{ fontSize: 13, color: "#fca5a5", flexShrink: 0 }} />
              <Typography sx={{ fontSize: 12, color: "#fca5a5" }}>
                {counters.correctivos_vencen_7_dias} correctivos vencen en 7 días
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Loading spinner ───────────────────────────────────────────────────────────

function TabLoading() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress size={28} />
    </Box>
  );
}

// ─── DashboardPage ─────────────────────────────────────────────────────────────

const SIDEBAR_MINI = 60;

export default function DashboardPage() {
  const [activeSection, setActiveSection] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { reloadKey } = useReload();
  const { plantaSeleccionada } = usePlanta();
  useVencimientoAlertas(plantaSeleccionada);

  const [counters, setCounters] = useState({
    total_equipos: null,
    total_ordenes: null,
    total_vencimientos: null,
    total_mantenimientos: null,
    correctivos_vencen_7_dias: null,
  });

  useEffect(() => {
    fetch(
      UrlApi +
        "/mp_equipos/contadores/" +
        (plantaSeleccionada ? `?planta_id=${plantaSeleccionada}` : "")
    )
      .then((res) => res.json())
      .then((result) => setCounters(result))
      .catch((err) => console.error("Error al obtener contadores", err));
  }, [reloadKey, plantaSeleccionada]);

  useEffect(() => {
    logEventos(
      "acceso al menú Mantenimiento Plantas",
      "Plantas y Depos",
      "Mantenimiento Plantas",
      localStorage.getItem("user")
    );
  }, []);

  const currentSidebarWidth = sidebarCollapsed ? SIDEBAR_MINI : SIDEBAR_WIDTH;

  const sidebarProps = {
    activeSection,
    onSelectSection: (id) => { setActiveSection(id); setMobileOpen(false); },
    counters,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Navbar />

      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        {/* Desktop sidebar — permanent, collapsible */}
        <Box
          sx={{
            width: currentSidebarWidth,
            flexShrink: 0,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            transition: "width 0.22s ease",
            overflow: "hidden",
          }}
        >
          <SidebarContent
            {...sidebarProps}
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed((p) => !p)}
          />
        </Box>

        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { width: SIDEBAR_WIDTH, border: "none", bgcolor: "#0f172a" },
          }}
        >
          <SidebarContent {...sidebarProps} collapsed={false} onToggle={null} />
        </Drawer>

        {/* Main content */}
        <Box
          sx={{
            flexGrow: 1,
            bgcolor: "#f8fafc",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflowY: "auto",
          }}
        >
          {/* Mobile top bar */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.25,
              bgcolor: "#ffffff",
              borderBottom: "1px solid #e2e8f0",
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <IconButton size="small" onClick={() => setMobileOpen(true)}>
              <MenuIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle2" fontWeight={600} color="#0f172a">
              {NAV_ITEMS[activeSection]?.label}
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 }, flexGrow: 1 }}>
            {/* Section header (desktop only) */}
            <Box sx={{ mb: 2.5, display: { xs: "none", md: "block" } }}>
              <Typography variant="h6" fontWeight={700} color="#0f172a" gutterBottom={false}>
                {NAV_ITEMS[activeSection]?.label}
              </Typography>
              <Typography variant="caption" color="#64748b">
                {plantaSeleccionada
                  ? "Datos filtrados por planta seleccionada"
                  : "Seleccioná una planta en el menú lateral para filtrar"}
              </Typography>
            </Box>

            {/* Stat cards (hidden on KPIs tab) */}
            {activeSection !== 5 && (
              <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap", alignItems: "flex-end" }}>
                {STATS.map(({ key, label, Icon, gradient }, i) => (
                  <StatCard key={key} label={label} Icon={Icon} value={counters[key]} gradient={gradient} delay={i * 70} />
                ))}
                <Box sx={{ ml: "auto", alignSelf: "center" }}>
                  <NotificarEncargadoButton />
                </Box>
              </Box>
            )}

            {/* Animated content area */}
            <Box
              key={activeSection}
              sx={{
                "@keyframes sectionIn": {
                  "0%": { opacity: 0, transform: "translateY(10px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" },
                },
                animation: "sectionIn 0.22s ease-out both",
              }}
            >
              <Suspense fallback={<TabLoading />}>
                {activeSection === 0 && <PlantSectionsList />}
                {activeSection === 1 && <EquipmentTable />}
                {activeSection === 2 && <WorkOrdersList />}
                {activeSection === 3 && <ExpirationsList />}
                {activeSection === 4 && <CorrectiveMaintenanceList />}
                {activeSection === 5 && <KpisDashboard />}
              </Suspense>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
